/* eslint-disable sonarjs/cognitive-complexity */
import { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useTypedSelector } from '../../stateStore'
import { reduxServices } from '../../reducers/reduxServices'
import { DefaultUserRoleFeature } from '../../types/Settings/UserRolesConfiguration/UserRolesConfigurationTypes'

export type SystemPermissionType = {
  featureId: number
  name: string
  viewaccess: boolean
  createaccess: boolean
  updateaccess: boolean
  deleteaccess: boolean
  type: string
}

export type UsePermissionType = {
  permissions: SystemPermissionType
  forbidden: () => void
  initializing: boolean
}

export enum PermissionEnum {
  View = 'viewaccess',
  Create = 'createaccess',
  Update = 'updateaccess',
  Delete = 'deleteaccess',
}

function main(
  featureName: string,
  otherCondition = true,
  permission = PermissionEnum.View,
  featId: number | null = null,
): UsePermissionType {
  const forbiddenUrl = '../forbidden'

  const history = useHistory()

  const userAccessToFeaturesByUser = useTypedSelector(
    reduxServices.userRolesConfigurations.selectors.userPermissionsToFeatures,
  )
  const userHasLoadedPermissions = useTypedSelector(
    reduxServices.userRolesConfigurations.selectors.userHasLoadedPermissions,
  )
  const hasFeatId = !!featId
  const hasPermission = userHasLoadedPermissions && !hasFeatId

  const userAccess = userAccessToFeaturesByUser?.find(
    (feature) => feature.name === featureName && feature[permission] === true,
  )

  // Only initiate if feat id was specified for its permission
  // Will not run the Array.find() function necessarily for optimization
  let userAccessById: DefaultUserRoleFeature | undefined = undefined
  if (hasFeatId) {
    userAccessById = userAccessToFeaturesByUser?.find(
      (feature) => feature.featureId === featId,
    )
  }

  const [initializing, setInitializing] = useState<boolean>(false)

  useEffect(() => {
    let startTimeoutId: NodeJS.Timeout
    let endTimeoutId: NodeJS.Timeout
    if (hasPermission) {
      startTimeoutId = setTimeout(() => {
        setInitializing(true)
      }, 1000)
      const hasUserConditions = !!userAccess && userAccess[permission]
      if (!hasUserConditions && otherCondition) {
        history.push(forbiddenUrl)
      }
      endTimeoutId = setTimeout(() => {
        setInitializing(false)
      }, 1000)
    }

    return () => {
      clearTimeout(startTimeoutId)
      clearTimeout(endTimeoutId)
      setInitializing(false)
    }
  }, [hasPermission, userAccessToFeaturesByUser, userAccess])

  useEffect(() => {
    let startTimeoutId: NodeJS.Timeout
    let endTimeoutId: NodeJS.Timeout
    if (userHasLoadedPermissions && hasFeatId) {
      startTimeoutId = setTimeout(() => {
        setInitializing(true)
      }, 1000)
      const hasUserConditions = !!userAccessById && userAccessById[permission]
      if (!hasUserConditions) {
        history.push(forbiddenUrl)
      }
      endTimeoutId = setTimeout(() => {
        setInitializing(false)
      }, 1000)
    }

    // eslint-disable-next-line sonarjs/no-identical-functions
    return () => {
      clearTimeout(startTimeoutId)
      clearTimeout(endTimeoutId)
      setInitializing(false)
    }
  }, [
    hasFeatId,
    userHasLoadedPermissions,
    userAccessToFeaturesByUser,
    userAccessById,
  ])

  const mergePermissions = (): SystemPermissionType => {
    const userFeatureId = userAccess?.featureId ?? ''
    const userName = userAccess?.name ?? ''

    return {
      featureId: Number(userFeatureId),
      name: String(userName),
      viewaccess: Boolean(userAccess?.viewaccess),
      createaccess: Boolean(userAccess?.createaccess),
      updateaccess: Boolean(userAccess?.updateaccess),
      deleteaccess: Boolean(userAccess?.deleteaccess),

      type: 'User-Role',
    }
  }

  const forbidden = () => {
    history.push(forbiddenUrl)
  }

  return {
    permissions: mergePermissions(),
    forbidden,
    initializing,
  }
}

/**
 * Custom hook to manage permissions checking based on feature name and feature IDs.
 * @param {string} featureName - The name of the feature to check permissions for.
 * @param {featureIdType} featId - an object containing feature IDs for user and role.
 * @param {PermissionEnum} permission - The type of permission to check (default: PermissionEnum.View).
 * @returns An object containing permissions and helper functions.
 */
export function useFeatureIdPermissionToView(
  featureName: string,
  featId: number | null,
  permission = PermissionEnum.View,
): UsePermissionType {
  return main(featureName, true, permission, featId)
}

/**
 * Custom hook that checks if the user has permission to view a specific feature based on a condition.
 * @param {string} featureName - The name of the feature to check permission for.
 * @param {boolean} otherCondition - A boolean value representing an additional condition to check.
 * @param {PermissionEnum} permission - The type of permission to check (default: PermissionEnum.View).
 * @returns An object containing permissions and helper functions.
 */
export function usePermissionToViewWithCondition(
  featureName: string,
  otherCondition: boolean,
  permission = PermissionEnum.View,
): UsePermissionType {
  return main(featureName, otherCondition, permission)
}

/**
 * Custom hook to manage permissions checking based on feature name.
 * @param {string} featureName - The name of the feature to check permissions for.
 * @param {PermissionEnum} permission - The type of permission to check (default: PermissionEnum.View).
 * @returns An object containing permissions and helper functions.
 */
export default function usePermissionToView(
  featureName: string,
  permission = PermissionEnum.View,
): UsePermissionType {
  return main(featureName, true, permission)
}
